/**
 * SMS-delivered patient satisfaction survey (`BACKLOG.md` C21, `emr-po` A18). A fourth
 * patient-scoped-token consumer (`patientScopedTokens.ts`'s own header names this file directly),
 * reusing that token mechanism and `C10b`'s form-template engine rather than inventing either — see
 * `server/src/patientSurvey/`'s own header for the full module-boundary argument.
 *
 * **Two genuinely separate concerns live here, not one:**
 *
 *   1. **TCPA consent** (`patient_sms_consent_events`, an append-only ledger — `smsConsentEventResponseSchema`
 *      and friends below). A row is one point-in-time event: a staff member documenting how and when
 *      a patient consented to receive survey texts (`SMS_CONSENT_GRANT_METHODS`), or an opt-out
 *      (`SMS_CONSENT_REVOKE_METHODS`) — staff-recorded (a patient called and asked to stop) or
 *      patient-initiated through the survey link itself (`patient_web_optout` — see this module's own
 *      note on why there is no inbound-SMS-reply `STOP` handler yet). **Deliberately not modeled as a
 *      new `LegalDocumentType`** the way kiosk's consent-to-treat/notice-of-privacy-practices are: a
 *      TCPA consent event needs a phone number, a method, and a revocable point-in-time state, none of
 *      which map cleanly onto "accepted this document version" — forcing it into that shape would be
 *      matching the nearest existing pattern rather than the strongest fit
 *      (`claude-kit/rules/working-style.md`'s "follow the repo's best convention in scope, not the
 *      nearest line" cuts the other way here: the *best* convention for a revocable, append-only,
 *      point-in-time fact ledger is `patient_scoped_tokens`'/`legal_document_acceptances`' own
 *      "insert, never update" discipline, not the *document-acceptance* shape specifically).
 *      `isSmsConsentActive` is the one pure decision this whole gate reduces to: is the most recent
 *      event a grant?
 *   2. **The survey itself** — a `patient_survey` token resolves to a specific pinned `form_templates`
 *      version (never returned to staff in the token-create response; see `server/src/patientSurvey/
 *      service.ts`'s own header for why) and a `patient_survey_responses` row records the filled
 *      answers. **Deliberately not a `form_submissions` row** — that table's `submittedByUserId` is a
 *      `NOT NULL` foreign key into `users`, and every route reading/writing it is gated on
 *      `chart:read`/`chart:write` + `evaluateChartAccess`, the staff-authored-clinical-documentation
 *      shape `C10b`'s own header argues for. A patient completing a survey through an unauthenticated
 *      token holds neither a `users` row nor a staff session — the identical mismatch
 *      `patient_license_scans` already resolved for `C19` by writing its own sibling table rather than
 *      `patients` directly. `validateFormSubmissionValues` (`formTemplates.ts`) is still the actual
 *      structured-data enforcement reused unchanged; only the storage destination differs.
 */

import { z } from 'zod';

import { MAX_CONTACT_LENGTH } from './clinicalRequests.ts';
import { formFieldsSchema, formSubmissionValuesSchema } from './formTemplates.ts';

// --- TCPA consent ledger -------------------------------------------------------------------------

export const MAX_SMS_CONSENT_NOTES_LENGTH = 1000;

/** How a patient's *grant* of SMS-survey consent was captured — staff always records this, never the
 * patient directly (mirrors `emr-po` A16 decision 1's "a patient-scoped token is always
 * staff-initiated"). */
export const SMS_CONSENT_GRANT_METHODS = [
  'verbal',
  'written',
  'kiosk_checkin',
  'phone_call',
] as const;
export type SmsConsentGrantMethod = (typeof SMS_CONSENT_GRANT_METHODS)[number];

/**
 * How an opt-out was captured. `patient_web_optout` is the one path a patient can trigger directly —
 * a real, working "reply STOP"-equivalent reachable from the same survey link with no login, per
 * `BACKLOG.md` C21's own "easy STOP opt-out" requirement. **Literal inbound-SMS `STOP`-reply
 * processing is not built here** — that needs a real vendor's inbound-webhook contract to parse
 * against, and `C21`'s own brief scopes adding a real vendor out of this build entirely; the outbound
 * message body still says "Reply STOP to opt out" (`server/src/patientSurvey/smsSender.ts`) so the
 * carrier-facing convention is honest about what a future vendor integration will need to wire up, but
 * today the only two ways an opt-out actually lands are staff recording one by hand
 * (`staff_recorded` — a patient called and asked) and the web link (`patient_web_optout`).
 */
export const SMS_CONSENT_REVOKE_METHODS = ['staff_recorded', 'patient_web_optout'] as const;
export type SmsConsentRevokeMethod = (typeof SMS_CONSENT_REVOKE_METHODS)[number];

export const SMS_CONSENT_EVENT_TYPES = ['granted', 'revoked'] as const;
export type SmsConsentEventType = (typeof SMS_CONSENT_EVENT_TYPES)[number];

export const recordSmsConsentRequestSchema = z.strictObject({
  patientId: z.uuid(),
  phoneNumber: z.string().trim().min(1).max(MAX_CONTACT_LENGTH),
  method: z.enum(SMS_CONSENT_GRANT_METHODS),
  notes: z.string().trim().min(1).max(MAX_SMS_CONSENT_NOTES_LENGTH).nullable().default(null),
});
export type RecordSmsConsentRequest = z.infer<typeof recordSmsConsentRequestSchema>;

/**
 * A staff-recorded opt-out. No `phoneNumber` — unlike a grant, an opt-out is scoped to the *patient*
 * (stop sending survey texts to this patient at all), not to re-asserting which number it applies to;
 * `emr-po` A18 names TCPA revocation but not a multi-number-per-patient model, and building one would
 * be inventing scope no requirement asked for.
 */
export const recordSmsOptOutRequestSchema = z.strictObject({
  patientId: z.uuid(),
  notes: z.string().trim().min(1).max(MAX_SMS_CONSENT_NOTES_LENGTH).nullable().default(null),
});
export type RecordSmsOptOutRequest = z.infer<typeof recordSmsOptOutRequestSchema>;

export const smsConsentEventResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  phoneNumber: z.string().nullable(),
  eventType: z.enum(SMS_CONSENT_EVENT_TYPES),
  method: z.string(),
  recordedByUserId: z.uuid().nullable(),
  recordedByPatientTokenId: z.uuid().nullable(),
  notes: z.string().nullable(),
  occurredAt: z.string(),
  createdAt: z.string(),
});
export type SmsConsentEventResponse = z.infer<typeof smsConsentEventResponseSchema>;

export const smsConsentStatusResponseSchema = z.object({
  active: z.boolean(),
  events: z.array(smsConsentEventResponseSchema),
});
export type SmsConsentStatusResponse = z.infer<typeof smsConsentStatusResponseSchema>;

/** The one pure decision the whole TCPA gate reduces to. Pure and tested in isolation (`claude-kit/
 * rules/invariants.md`'s "business rules live in pure, tested modules") rather than inlined into
 * `server/src/patientSurvey/service.ts`'s SQL — mirrors `patientScopedTokens.ts`'s own
 * `isPatientScopedTokenUsable` shape exactly: a tiny, named, unit-testable boolean decision over
 * already-fetched facts, no database access. */
export function isSmsConsentActive(
  latestEvent: { readonly eventType: SmsConsentEventType } | null,
): boolean {
  return latestEvent !== null && latestEvent.eventType === 'granted';
}

// --- Survey token issuance -----------------------------------------------------------------------

export const PATIENT_SURVEY_TOKEN_ERROR_CODES = [
  'invalid_request',
  'not_found',
  /** The named encounter exists but has not reached `status: 'completed'` — a survey link is only
   * ever issued for a visit that has actually happened. */
  'encounter_not_completed',
  /** The named template version exists but is archived — a new survey link may not start against
   * it, matching `FORM_TEMPLATE_ERROR_CODES`' identical `template_archived` reasoning. */
  'template_archived',
  /** `isSmsConsentActive` returned false for this patient — the enforced TCPA gate. */
  'sms_consent_required',
  /** The patient has no phone number on file to send the link to. */
  'patient_missing_phone',
] as const;
export type PatientSurveyTokenErrorCode = (typeof PATIENT_SURVEY_TOKEN_ERROR_CODES)[number];

export const createPatientSurveyTokenRequestSchema = z.strictObject({
  patientId: z.uuid(),
  encounterId: z.uuid(),
  templateId: z.uuid(),
});
export type CreatePatientSurveyTokenRequest = z.infer<typeof createPatientSurveyTokenRequestSchema>;

/**
 * Deliberately carries no raw token — a genuine divergence from `kioskTokenResponseSchema`/
 * `licenseScanTokenResponseSchema`, both of which *do* return the raw bearer value because staff hand
 * it to a tablet directly in front of them. This token is delivered exactly once, over SMS
 * (`server/src/patientSurvey/service.ts`'s `issuePatientSurveyToken`) — echoing it back into the
 * staff-facing API response too would be a second egress path for the same credential with no
 * corresponding need, the same "a credential shown to a person goes in the response body, once"
 * discipline `claude-kit/rules/invariants.md`'s "Secrets travel once" already states for this exact
 * shape of mistake.
 */
export const patientSurveyTokenResponseSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PatientSurveyTokenResponse = z.infer<typeof patientSurveyTokenResponseSchema>;

export const createPatientSurveyTokenResponseSchema = z.object({
  token: patientSurveyTokenResponseSchema,
});
export type CreatePatientSurveyTokenResponse = z.infer<
  typeof createPatientSurveyTokenResponseSchema
>;

// --- The token-scoped survey surface itself -------------------------------------------------------

/**
 * What the survey-completion page renders — the pinned template's own fields, not a live/current
 * version, matching `formTemplates.ts`'s own versioning guarantee: a template edited after this token
 * was issued must never change what this specific patient is shown.
 *
 * Deliberately carries no `completed` flag — unlike kiosk's own check-in status, a `patient_survey`
 * token is refused outright (401) by `requireSurveyToken` the instant it is marked complete
 * (`isPatientScopedTokenUsable` treats `completedAt` as terminal), so this route can never actually be
 * reached again once a submission has landed; a `completed` field would be dead, always-`false` state.
 */
export const patientSurveyViewResponseSchema = z.object({
  templateId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  fields: formFieldsSchema,
  expiresAt: z.string(),
});
export type PatientSurveyViewResponse = z.infer<typeof patientSurveyViewResponseSchema>;

export const submitPatientSurveyRequestSchema = z.strictObject({
  values: formSubmissionValuesSchema,
});
export type SubmitPatientSurveyRequest = z.infer<typeof submitPatientSurveyRequestSchema>;

export const submitPatientSurveyResponseSchema = z.object({ submitted: z.literal(true) });

export const patientSurveyOptOutResponseSchema = z.object({ optedOut: z.literal(true) });

export const PATIENT_SURVEY_SUBMIT_ERROR_CODES = ['invalid_request', 'invalid_values'] as const;
export type PatientSurveySubmitErrorCode = (typeof PATIENT_SURVEY_SUBMIT_ERROR_CODES)[number];

// --- Staff-visible responses ------------------------------------------------------------------------

export const patientSurveyResponseRecordSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  encounterId: z.uuid(),
  templateId: z.uuid(),
  values: formSubmissionValuesSchema,
  submittedAt: z.string(),
  createdAt: z.string(),
});
export type PatientSurveyResponseRecord = z.infer<typeof patientSurveyResponseRecordSchema>;

export const listPatientSurveyResponsesResponseSchema = z.object({
  responses: z.array(patientSurveyResponseRecordSchema),
});
